import type { Artifact, Axis } from '../types.ts';
import { cellFor, resolveCellEntry, vendorByCompany } from '../lib/artifact.ts';
import type { SelectedCell } from './ComparisonTable.tsx';
import { VerdictBadge } from './VerdictBadge.tsx';
import { MentionList } from './MentionList.tsx';
import { SourceChip } from './SourceChip.tsx';
import { AxisDemandBody } from './AxisDemandDetail.tsx';

/**
 * Clicking a cell expands feature-vs-feature: my capability side-by-side with the cited vendor
 * feature, its subfeatures expandable beneath, each with its own mentions (spec §3).
 * Rendered per the mockup: signal left rule, "You wrote / They ship" columns over a hairline,
 * cited evidence as `.ev` blocks, the adjudicator's note as a brass strip.
 *
 * Closes with the axis's demand — supply and demand for one capability read in one place. It is
 * the same block the row-header key opens, and it states "not gathered" for a run with no
 * demand pass rather than leaving a silence that reads as no demand.
 */
export function CellDetail({
  artifact,
  axes,
  selected,
  highlightQuery = '',
}: {
  artifact: Artifact;
  axes: Axis[];
  selected: SelectedCell;
  highlightQuery?: string;
}): JSX.Element | null {
  const axis = axes.find((a) => a.capability === selected.axis);
  const cell = cellFor(artifact.matrix, selected.axis, selected.company);
  const vendor = vendorByCompany(artifact, selected.company);
  if (!axis || !cell || !vendor) return null;

  const resolved = resolveCellEntry(vendor, cell);

  return (
    <section className="artifact-section">
      <div className="tick">
        <span className="label">
          § Cell detail · {axis.capability} × {selected.company}
        </span>
      </div>
      <div className="panel cell-detail">
        <div className="cell-detail-header">
          <VerdictBadge verdict={cell.verdict} />
          <span className="cell-detail-company">{selected.company}</span>
        </div>

        <div className="cell-detail-columns">
          <div className="cell-detail-col">
            <span className="label">You wrote</span>
            <h4>{axis.capability}</h4>
            {axis.description && <p className="muted">{axis.description}</p>}
          </div>

          <div className="cell-detail-col">
            <span className="label">They ship</span>
            {resolved ? (
              <>
                <h4>
                  {resolved.subfeature ? resolved.subfeature.name : resolved.feature.name}
                  {resolved.subfeature && <span className="muted"> (under {resolved.feature.name})</span>}
                </h4>
                <p className="cell-detail-product muted">{resolved.feature.product}</p>
                <MentionList
                  mentions={resolved.subfeature ? resolved.subfeature.mentions : resolved.feature.mentions}
                  highlightQuery={highlightQuery}
                />

                {!resolved.subfeature && resolved.feature.subfeatures.length > 0 && (
                  <div className="cell-detail-subfeatures">
                    <h5 className="label">Subfeatures</h5>
                    {resolved.feature.subfeatures.map((s) => (
                      <details key={s.name}>
                        <summary>{s.name}</summary>
                        <MentionList mentions={s.mentions} highlightQuery={highlightQuery} />
                      </details>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <p className="muted">{cell.note ?? 'no cited entry'}</p>
            )}
          </div>
        </div>

        {cell.evidence.length > 0 && (
          <div className="cell-detail-evidence">
            <span className="label">Cited evidence</span>
            {cell.evidence.map((e, i) => (
              <div key={i} className={`ev${e.third_party ? ' ev-third-party' : ''}`}>
                <q>{e.quote}</q>
                <span className="src">
                  <a href={e.url} target="_blank" rel="noreferrer" className="bare">
                    {e.url}
                  </a>
                  <SourceChip thirdParty={e.third_party} sourceKind={e.source_kind} />
                </span>
              </div>
            ))}
          </div>
        )}

        {cell.note && resolved && (
          <div className="cell-detail-footer">
            <p className="cell-note">Note: {cell.note}</p>
          </div>
        )}

        <div className="cell-detail-demand">
          <span className="label">Demand for this capability</span>
          <AxisDemandBody artifact={artifact} axis={axis.capability} highlightQuery={highlightQuery} />
        </div>
      </div>
    </section>
  );
}
